"use client";
import { productSearch } from "@lib/data/products";
import { getProductPrice } from "@lib/util/get-product-price";
import { useProgressRouter } from "@lib/util/navigation";
import { Input } from "@medusajs/ui";
import LocalizedClientLink from "@modules/common/components/localized-client-link";
import Spinner from "@modules/common/icons/spinner";
import PreviewPrice from "@modules/products/components/product-preview/price";
import Thumbnail from "@modules/products/components/thumbnail";
import { Clock9, Search } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ExtendedStoreProduct } from "types/global";

const MAX_HISTORY = 5;

function SearchInput() {
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useProgressRouter();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<ExtendedStoreProduct[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isLoadingBtn, setIsLoadingBtn] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const history = JSON.parse(localStorage.getItem("searchHistory") || "[]");
    setSearchHistory(history);
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const fetchProducts = async () => {
      setIsLoadingSearch(true);

      try {
        const params = {
          query: searchTerm.trim(),
          queryParams: {
            limit: 5,
          },
        };
        const results = await productSearch(params);
        setSearchResults(results.response.products);
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setIsLoadingSearch(false);
      }
    };

    const debounce = setTimeout(fetchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const handleSearch = () => {
    setShowDropdown(false);
    setIsLoadingBtn(true);

    if (!searchTerm.trim()) {
      setTimeout(() => {
        setIsLoadingBtn(false);
        router.push(`/store`);
      }, 500);
      return;
    }

    const updatedHistory = [searchTerm, ...searchHistory.filter((item) => item !== searchTerm)].slice(0, MAX_HISTORY);

    setSearchHistory(updatedHistory);

    localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));

    setTimeout(() => {
      setIsLoadingBtn(false);
      router.push(`/product-search?q=${encodeURIComponent(searchTerm)}`);
    }, 500);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowDropdown(false);
        if (showDropdown) {
          inputRef.current?.blur();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="lg:flex hidden">
      <div className="relative w-full" ref={searchRef}>
        <Input
          ref={inputRef}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          placeholder="Tìm kiếm sản phẩm, thương hiệu..."
          className="bg-white placeholder:text-gray-300 rounded-lg h-10 pr-12"
        />
        <button
          onClick={handleSearch}
          className="absolute right-1 top-1/2 -translate-y-1/2 bg-primary-600 p-2 rounded-lg"
        >
          <div className="text-white">{!isLoadingBtn ? <Search className="w-5 h-5" /> : <Spinner />}</div>
        </button>

        {/* Dropdown suggestion */}
        {showDropdown && (
          <div className="absolute top-12 left-0 w-full bg-white rounded-md shadow-2xl">
            {!searchTerm.trim() ? (
              searchHistory.length > 0 && (
                <div className="p-3">
                  <p className="text-[#242533] text-sm mb-2 font-semibold">Tìm kiếm gần đây</p>
                  {searchHistory.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        setSearchTerm(item);
                        setShowDropdown(true);
                      }}
                      className="p-2 hover:bg-gray-100 cursor-pointer text-sm flex rounded-md items-center gap-2"
                    >
                      <div>
                        <Clock9 size={16} />
                      </div>
                      <div className="truncate">{item}</div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="p-3">
                <p className="text-[#242533] text-sm mb-2 font-semibold">Sản phẩm gợi ý</p>
                {isLoadingSearch ? (
                  <div className="flex justify-center items-center py-4">
                    <Spinner className="animate-spin" />
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((product) => {
                    const { cheapestPrice } = getProductPrice({
                      product,
                    });
                    return (
                      <div
                        onClick={() => setShowDropdown(false)}
                        key={product.id}
                        className="max-h-[500px] overflow-y-scroll"
                      >
                        <LocalizedClientLink
                          href={`/products/${product.handle}`}
                          className="flex w-full gap-2 p-1 mb-1 hover:bg-gray-100 rounded-md cursor-pointer"
                        >
                          <div className="flex-1">
                            <div className="text-[#434657] text-[14px] line-clamp-2">{product.title}</div>
                            <div className="flex gap-2">
                              <div className="text-[#242533] text-[15px] font-semibold">
                                {cheapestPrice && (
                                  <PreviewPrice price={cheapestPrice} />
                                  // <>{cheapestPrice?.calculated_price}</>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="w-[50px] h-[50px] shrink-0">
                            {product.images && product.images.length > 0 ? (
                              <div>
                                <Image src={product.images[0]?.url} alt={""} width={50} height={50} />
                              </div>
                            ) : (
                              <Thumbnail size="square" />
                            )}
                          </div>
                        </LocalizedClientLink>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex justify-center items-center py-4">Không tìm thấy sản phẩm</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchInput;
